// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
  // Try to get from localStorage first (fallback)
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export async function fetchCustomers(filters = {}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query parameters from filters
  const queryParams = new URLSearchParams();
  if (filters.projectName) {
    queryParams.append('project', filters.projectName['_id']);
  }
  if (filters.Date) {
    queryParams.append('date', filters.Date);
  }
  // Add pagination parameters
  if (filters.page) {
    queryParams.append('page', filters.page);
  }
  if (filters.limit) {
    queryParams.append('limit', filters.limit);
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}api/v1/dashboard/customer${queryString ? `?${queryString}` : ''}`;

  console.log('Making customers request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch customers';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function fetchSettledCustomers(filters = {}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query parameters from filters
  const queryParams = new URLSearchParams();
  if (filters.projectName) {
    queryParams.append('project', filters.projectName['_id']);
  }
  if (filters.Date) {
    queryParams.append('date', filters.Date);
  }
  // Add pagination parameters
  if (filters.page) {
    queryParams.append('page', filters.page);
  }
  if (filters.limit) {
    queryParams.append('limit', filters.limit);
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}api/v1/dashboard/customer/settled${queryString ? `?${queryString}` : ''}`;

  console.log('Making settled customers request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch settled customers';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function fetchDebitCustomers(filters = {}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query parameters from filters
  const queryParams = new URLSearchParams();
  if (filters.projectName) {
    queryParams.append('project', filters.projectName['_id']);
  }
  if (filters.Date) {
    queryParams.append('date', filters.Date);
  }
  // Add pagination parameters
  if (filters.page) {
    queryParams.append('page', filters.page);
  }
  if (filters.limit) {
    queryParams.append('limit', filters.limit);
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}api/v1/dashboard/customer/debit${queryString ? `?${queryString}` : ''}`;

  console.log('Making debit customers request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch debit customers';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function fetchCustomerDetails(customerId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making customer details request to:', `${API_BASE_URL}api/v1/dashboard/customer/${customerId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/customer/${customerId}`, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch customer details';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function createCustomer(formData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {};

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Don't set Content-Type for FormData, let the browser set it with boundary
  console.log('Making create customer request to:', `${API_BASE_URL}api/v1/dashboard/customer/create`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/customer/create`, {
    method: 'POST',
    headers,
    body: formData
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to create customer';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function updateCustomer(customerId, formData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {};

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Don't set Content-Type for FormData, let the browser set it with boundary
  console.log('Making update customer request to:', `${API_BASE_URL}api/v1/dashboard/customer/update/${customerId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/customer/update/${customerId}`, {
    method: 'PATCH',
    headers,
    body: formData
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to update customer';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function searchCustomers(swagId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making search customers request to:', `${API_BASE_URL}api/v1/dashboard/customer/search`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/customer/search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ "swag_id": swagId })
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to search customers';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteCustomer(customerId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making delete customer request to:', `${API_BASE_URL}api/v1/dashboard/customer/delete/${customerId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/customer/delete/${customerId}`, {
    method: 'DELETE',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to delete customer';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function exportCustomers(status = 'all') {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  status = status.trim();
  console.log(status);
  const url = `${API_BASE_URL}api/v1/dashboard/customer/export?status=${status}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to export customers';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  // Get the blob from the response
  const blob = await response.blob();
  // Create a download link
  const urlObj = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = urlObj;
  a.download = `customers_export_${status}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(urlObj);
  document.body.removeChild(a);
  return { success: true };
}

// Fetch customer units
export async function fetchCustomerUnits(customerId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/customer/get-customer-unit?customer_id=${customerId}`;
  console.log('Making fetch customer units request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch customer units';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

// Export customer unit template
export async function exportCustomerUnitTemplate(customerId, unitId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/customer/export-customer-unit-template?customer_id=${customerId}&unit_id=${unitId}`;
  console.log('Making export customer unit template request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to export customer unit template';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  // Get the blob from the response
  const blob = await response.blob();
  // Create a download link
  const urlObj = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = urlObj;
  a.download = `customer_unit_template_${customerId}_${unitId}_${new Date().toISOString().split('T')[0]}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(urlObj);
  document.body.removeChild(a);
  return { success: true };
}